import { useCanRead } from '@artic/api-client';
import { useFeatureFlag } from '@artic/ui/feature-flags';
import { OperatingCostsIcon } from '@artic/ui/icons/nav';
import { getQualityUrl } from '@artic/ui/lib/environment';
import { useTranslation } from 'react-i18next';

import type { LinkItemProps } from '~/layout/SidebarLayout';

/**
 * Neo's Operating Costs nav section (ENG-871), mirrored into the Hub so partner users can
 * reach it from here too (ENG-1017): the same items, order and flags as `quality-sidebar.tsx`.
 *
 * The views are Neo pages, so every item is an absolute href. A flag that is off disables
 * its items rather than hiding them, as in Neo, and nothing is admin-gated. ENG-945:
 * `cost_to_produce` gates only Cost to Produce; the other four sit behind `operating_costs`.
 */
export function useOperatingCostsNavGroup(): LinkItemProps {
  const { t } = useTranslation('translation');
  const operatingCosts = useFeatureFlag('operating_costs');
  const costToProduce = useFeatureFlag('cost_to_produce');
  const canReadCostToProduce = useCanRead('cost_to_produce');
  const views = `${getQualityUrl()}/operating-costs`;

  return {
    name: t('nav.operating_costs'),
    icon: OperatingCostsIcon,
    hide: !canReadCostToProduce,
    children: [
      { name: t('nav.overheads'), href: `${views}/overheads`, disabled: !operatingCosts },
      { name: t('nav.support'), href: `${views}/support`, disabled: !operatingCosts },
      { name: t('nav.value_adding'), href: `${views}/value-adding`, disabled: !operatingCosts },
      { name: t('nav.labour'), href: `${views}/labor`, disabled: !operatingCosts },
      {
        name: t('nav.cost_to_produce'),
        href: `${views}/cost-to-produce`,
        disabled: !costToProduce,
      },
    ],
  };
}
